import React, { ReactChild } from 'react'
import { getRandom, Situation } from '../data/situations'

export type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user: User
  experiment: Experiment
  submitted: boolean
  language: 'es' | 'en'
  situations_config: {
    situations_obligatorios: Situation[]
    situations_opcionales: Situation[]
    orden: string[]
    config: {
      fork_tren: string
      fork_orden: string
      orden_obligatorios: string[]
      orden_opcionales: string[]
    }
  }
}

export type Situations = {
  '1_1_aceptabilidad': number
  '1_1_confianza': number
  '1_1_emotividad': number
  '1_2_aceptabilidad': number
  '1_3_aceptabilidad': number
  '2_aceptabilidad': number
  '2_confianza': number
  '2_emotividad': number
  '3_1_aceptabilidad': number
  '3_1_confianza': number
  '3_1_emotividad': number
  '3_2_aceptabilidad': number
  '3_3_aceptabilidad': number
  '4_aceptabilidad': number
  '4_confianza': number
  '4_emotividad': number
  '5_aceptabilidad': number
  '5_confianza': number
  '5_emotividad': number
  '6_aceptabilidad': number
  '6_confianza': number
  '6_emotividad': number
  '7_aceptabilidad': number
  '7_confianza': number
  '7_emotividad': number
  '8_aceptabilidad': number
  '8_confianza': number
  '8_emotividad': number
  '9_aceptabilidad': number
  '9_confianza': number
  '9_emotividad': number
  '10_aceptabilidad': number
  '10_confianza': number
  '10_emotividad': number
  orden: string[]
}

export type Oxford = {
  oxford_1: string
  oxford_2: string
  oxford_3: string
  oxford_4: string
  oxford_5: string
  oxford_6: string
  oxford_7: string
  oxford_8: string
  oxford_9: string
  orden: string[]
  puntaje: number
  porcentaje: number
}

export type ParaTerminar = {
  edad: string | number
  genero: string
  nacionalidad: string
  pais_actual: string
  checkbox_covid: number[]
}

export type Experiment = {
  situations?: Situations
  oxford?: Oxford
  para_terminar?: ParaTerminar
}

// const es_en_language = (lang: string) => (lang.includes('es') ? 'es' : 'en')

const initialAppContextData: () => AppContextData = () => {
  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    auth: false,
    visited: [],
    experiment: {},
    situations_config: getRandom(),
    user: {
      accessToken: '',
      experimentDone: false,
      prevAllowsNews: null,
      service: 'email',
    },
    submitted: false,
    language: 'es', // es_en_language(localStorage.getItem('i18nextLng') || 'en'),
  }
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type SetLanguage = {
  type: 'SET_LANGUAGE'
  language: 'es' | 'en'
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type SubmitSituations = {
  type: 'SUBMIT_SITUATIONS'
  data: Situations
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type SubmitOxford = {
  type: 'SUBMIT_OXFORD'
  data: Oxford
}

type SubmitParaTermiar = {
  type: 'SUBMIT_PARA_TERMINAR'
  data: ParaTerminar
}

type Submited = {
  type: 'SUBMITTED'
}

type ReducerAction =
  | Login
  | Visit
  | AcceptTerms
  | SetLanguage
  | SubmitParaTermiar
  | SubmitSituations
  | SubmitOxford
  | Submited

const mutateExperimentState = (state: AppContextData) => <
  T extends keyof Experiment
>(
  key: T
) => (data: Experiment[T]) => ({
  ...state,
  experiment: {
    ...state.experiment,
    [key]: data,
  },
})

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'SUBMITTED': {
      return {
        ...state,
        submitted: true,
      }
    }
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'SET_LANGUAGE': {
      return { ...state, language: action.language }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SUBMIT_PARA_TERMINAR': {
      return mutateExperimentState(state)('para_terminar')(action.data)
    }
    case 'SUBMIT_SITUATIONS': {
      return mutateExperimentState(state)('situations')(action.data)
    }
    case 'SUBMIT_OXFORD': {
      return mutateExperimentState(state)('oxford')(action.data)
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
